/**
 * RecordListPanel
 *
 * Left pane of the Records page: entity picker + New Record, typeahead search,
 * and status/group filters. The data-bound region (selection bar + list) is
 * RecordResults, which owns the useRecordsBrowser subscription — keeping the
 * record-data subscription out of this component so editing a record never
 * re-renders the filter controls.
 */
import { FC, useCallback, useContext, useState, KeyboardEvent } from 'react';
import { Button, Input, Select, theme } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';

import { RecordStatus } from '#types/System.Parameters.types';
import { entityRecordActions } from '@/store';
import { EntitiesContext } from '@/providers/artifacts/EntitiesContext';
import { UserContext } from '@/providers/UserContext';
import { panelViewportClamp } from '@/store/uiState';
import FlexCol from '../atoms/FlexCol';
import FlexBox from '../atoms/FlexBox';
import RecordFilterBar from './RecordFilterBar';
import RecordResults from './RecordResults';

interface RecordListPanelProps {
    /** Pinned pane width (px) from the divider, or '100%' when RecordsBrowser stacks below md. */
    width: number | '100%';
    entityId: string;
    recordId: string | undefined;
    /** Initial search text from the URL; the input owns its value locally thereafter. */
    initialQuery: string;
    onQueryChange: (value: string) => void;
    statusFilters: RecordStatus[];
    setStatusFilters: (statuses: RecordStatus[]) => void;
    groupIds: string[];
    setGroupIds: (groupIds: string[]) => void;
    onOpenRecord: (recordId: string) => void;
    onSelectEntity: (entityId: string) => void;
}

const RecordListPanel: FC<RecordListPanelProps> = ({
    width,
    entityId,
    recordId,
    initialQuery,
    onQueryChange,
    statusFilters,
    setStatusFilters,
    groupIds,
    setGroupIds,
    onOpenRecord,
    onSelectEntity,
}) => {
    const { token } = theme.useToken();
    const { entitiesArray } = useContext(EntitiesContext);
    const { user } = useContext(UserContext);
    const [isCreating, setIsCreating] = useState(false);
    const [query, setQueryLocal] = useState(initialQuery);

    const setQuery = useCallback((value: string) => {
        setQueryLocal(value);
        onQueryChange(value);
    }, [onQueryChange]);

    const entityOptions = entitiesArray.map((entity) => ({
        value: entity.docId,
        label: entity.description?.shortLabel || entity.docId,
    }));

    const handleCreate = useCallback(async () => {
        setIsCreating(true);
        try {
            const newId = await entityRecordActions.createEntityRecord(entityId, user?.uid, groupIds[0]);
            onOpenRecord(newId);
        } finally {
            setIsCreating(false);
        }
    }, [entityId, user?.uid, groupIds, onOpenRecord]);

    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') setQuery('');
    };

    return (
        <FlexCol
            gap={0}
            noGrow
            style={{
                width,
                maxWidth: panelViewportClamp('recordListPane'),
                flexShrink: 0,
                height: '100%',
                minHeight: 0,
                backgroundColor: token.colorBgLayout,
            }}
        >
            <FlexCol gap={10} noGrow style={{ width: '100%', padding: 20, boxSizing: 'border-box' }}>
                <FlexBox noGrow gap={5} style={{ width: '100%' }}>
                    <Select
                        variant="filled"
                        size="small"
                        showSearch={{ optionFilterProp: 'label' }}
                        placeholder="Select record type"
                        value={entityId || undefined}
                        onChange={onSelectEntity}
                        options={entityOptions}
                        style={{ flex: 1 }}
                        aria-label="Select entity"
                    />
                    <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        loading={isCreating}
                        disabled={!entityId}
                        aria-label="Create new record"
                    />
                </FlexBox>
                <Input
                    variant="filled"
                    size="small"
                    prefix={<SearchOutlined />}
                    placeholder="Search records..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    disabled={!entityId}
                    allowClear
                    aria-label="Search records"
                />
                <RecordFilterBar
                    entityId={entityId}
                    statusFilters={statusFilters}
                    onStatusChange={setStatusFilters}
                    groupIds={groupIds}
                    onGroupsChange={setGroupIds}
                    disabled={!entityId}
                />
            </FlexCol>
            <RecordResults
                entityId={entityId}
                recordId={recordId}
                query={query}
                statusFilters={statusFilters}
                groupIds={groupIds}
                onOpenRecord={onOpenRecord}
            />
        </FlexCol>
    );
};

export default RecordListPanel;
